import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { RefreshCw, Users, Building2, TrendingUp, Clock, AlertTriangle, CheckCircle2 } from "lucide-react";

/**
 * Directive 4 Amendment — Super-admin trial funnel analytics.
 *
 * Renders a two-column funnel (Obondo individual traders + Institutional
 * agencies) plus a top-line KPI strip aggregating both tracks. Data comes
 * from `GET /api/admin/trials/analytics`.
 */
export default function TrialAnalyticsCard() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get("/admin/trials/analytics");
            setData(r.data);
        } catch (e) {
            toast.error("Trial analytics failed: " + (e?.response?.data?.detail || e.message));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    if (loading && !data) {
        return (
            <div className="rounded-xl border border-border bg-card p-6 text-sm text-muted-foreground" data-testid="trial-analytics-loading">
                Loading trial funnel…
            </div>
        );
    }
    if (!data) {
        return (
            <div className="rounded-xl border border-border bg-card p-6" data-testid="trial-analytics-empty">
                <Button size="sm" onClick={load} data-testid="trial-analytics-retry-btn">
                    <RefreshCw className="mr-2 h-4 w-4" /> Retry
                </Button>
            </div>
        );
    }

    const t = data.totals || {};
    const ob = data.obondo?.funnel || {};
    const it = data.institutional?.funnel || {};

    return (
        <section className="space-y-4" data-testid="trial-analytics-card">
            {/* KPI strip */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <Kpi label="Active trials"      value={t.active_trials ?? 0} icon={Clock}          tone="sky"    testid="kpi-active" />
                <Kpi label="In grace window"    value={t.in_grace ?? 0}      icon={AlertTriangle} tone="amber"  testid="kpi-grace" />
                <Kpi label="Converted (paid)"   value={t.converted_paid ?? 0} icon={CheckCircle2}  tone="emerald" testid="kpi-converted" />
                <Kpi label="Expired · lost"     value={t.expired_lost ?? 0}   icon={TrendingUp}    tone="rose"   testid="kpi-expired" />
            </div>

            {/* Track breakdown */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <TrackPanel
                    icon={Users}
                    title="Obondo · individual traders"
                    subtitle="7-day Advanced trial + 3-day grace recovery"
                    funnel={ob}
                    breakdown={data.obondo?.by_status || {}}
                    testid="obondo-panel"
                />
                <TrackPanel
                    icon={Building2}
                    title="Institutional · agencies"
                    subtitle="7-day CMA/MoE trial · manual wire conversion"
                    funnel={it}
                    breakdown={data.institutional?.by_plan_type || {}}
                    testid="institutional-panel"
                />
            </div>

            <div className="flex justify-end">
                <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="trial-analytics-refresh-btn">
                    <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
                </Button>
            </div>
        </section>
    );
}

/* ── sub-components ─────────────────────────────────────────────── */

function Kpi({ label, value, icon: Icon, tone, testid }) {
    const toneMap = {
        sky:     "border-sky-500/40 bg-sky-500/10 text-sky-500",
        amber:   "border-amber-500/40 bg-amber-500/10 text-amber-500",
        emerald: "border-emerald-500/40 bg-emerald-500/10 text-emerald-500",
        rose:    "border-rose-500/40 bg-rose-500/10 text-rose-500",
    };
    return (
        <div className={`rounded-xl border p-4 ${toneMap[tone] || "border-border bg-card"}`} data-testid={testid}>
            <div className="flex items-center gap-2 mb-1">
                <Icon className="h-4 w-4" />
                <span className="text-xs uppercase tracking-wide font-semibold opacity-80">{label}</span>
            </div>
            <div className="text-3xl font-bold proportional-nums whitespace-nowrap">{value}</div>
        </div>
    );
}

function TrackPanel({ icon: Icon, title, subtitle, funnel, breakdown, testid }) {
    const rate = funnel.conversion_rate_pct ?? 0;
    return (
        <div className="rounded-xl border border-border bg-card p-5" data-testid={testid}>
            <header className="flex items-start gap-3 mb-4">
                <div className="rounded-lg bg-primary/10 p-2 text-primary">
                    <Icon className="h-5 w-5" />
                </div>
                <div>
                    <h3 className="text-base font-semibold">{title}</h3>
                    <p className="text-xs text-muted-foreground">{subtitle}</p>
                </div>
            </header>

            <div className="space-y-1 text-sm">
                <Row label="Activated total"  value={funnel.activated_total ?? 0} testid={`${testid}-activated`} />
                <Row label="Still in trial"   value={funnel.still_in_trial ?? 0}  testid={`${testid}-in-trial`} />
                {"in_grace" in funnel && (
                    <Row label="In grace"      value={funnel.in_grace ?? 0}        testid={`${testid}-in-grace`} />
                )}
                <Row label="Converted"        value={funnel.converted ?? 0}        testid={`${testid}-converted`} accent="emerald" />
                <Row label="Expired · lost"   value={funnel.expired_lost ?? 0}     testid={`${testid}-expired`}  accent="rose" />
            </div>

            <div className="mt-4 pt-4 border-t border-border">
                <div className="flex justify-between items-baseline mb-1">
                    <span className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">Conversion rate</span>
                    <span className="text-2xl font-bold proportional-nums" data-testid={`${testid}-rate`}>{rate}%</span>
                </div>
                <div className="h-2 rounded-full bg-muted overflow-hidden">
                    <div
                        className="h-full bg-emerald-500 transition-all"
                        style={{ width: `${Math.min(100, Math.max(0, rate))}%` }}
                    />
                </div>
            </div>

            {Object.keys(breakdown).length > 0 && (
                <details className="mt-4 text-xs text-muted-foreground">
                    <summary className="cursor-pointer select-none hover:text-foreground">Raw status breakdown</summary>
                    <ul className="mt-2 space-y-1">
                        {Object.entries(breakdown).map(([k, v]) => (
                            <li key={k} className="flex justify-between">
                                <span className="font-mono">{k}</span>
                                <span className="proportional-nums">{v}</span>
                            </li>
                        ))}
                    </ul>
                </details>
            )}
        </div>
    );
}

function Row({ label, value, testid, accent }) {
    const accentClass = accent === "emerald"
        ? "text-emerald-500"
        : accent === "rose"
            ? "text-rose-500"
            : "text-foreground";
    return (
        <div className="flex justify-between items-center py-1" data-testid={testid}>
            <span className="text-muted-foreground">{label}</span>
            <span className={`font-semibold proportional-nums ${accentClass}`}>{value}</span>
        </div>
    );
}
